import React, { useRef, useState } from 'react';
import StaffEditor from './StaffEditor';
import PersonnelPdfButton from './PersonnelPdfButton';
import { compactTime, dayLongLabel } from './AgendaResponsiveView';
import useSwipeBack from '../../hooks/useSwipeBack';

function normalizeTimeText(value) {
  const raw = String(value || '').trim().replace('.', ':');
  if (!raw) return '';
  if (/^\d{1,2}$/.test(raw)) return `${String(Math.min(23, Math.max(0, Number(raw)))).padStart(2, '0')}:00`;
  const match = raw.match(/^(\d{1,2}):(\d{1,2})$/);
  if (!match) return raw;
  return `${String(Math.min(23, Math.max(0, Number(match[1])))).padStart(2, '0')}:${String(Math.min(59, Math.max(0, Number(match[2])))).padStart(2, '0')}`;
}

export default function AgendaMobileTaskEditor({
  task,
  isCreating,
  defaultDate,
  canEdit,
  canEditAssignments,
  personnel,
  assignmentModel,
  assignmentModelError,
  retryAssignmentModel,
  onCancel,
  onSave,
}) {
  const staffTabGroup = useRef({});
  const [form, setForm] = useState({
    task_date: task?.task_date?.slice(0, 10) || defaultDate || '',
    title: task?.title || '',
    time: compactTime(task?.time),
    end_time: compactTime(task?.end_time),
    owner: task?.owner || '',
    notes: task?.notes || '',
    status: task?.status || 'pendiente',
    event_id: task?.event_id || null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const disabled = !canEdit || saving;

  function setField(key) {
    return event => setForm(prev => ({ ...prev, [key]: event.target.value }));
  }

  async function submit() {
    setSaving(true);
    setError('');
    try {
      if (!form.title.trim()) throw new Error('El titulo es obligatorio.');
      if (!form.task_date) throw new Error('La fecha es obligatoria.');
      await onSave({
        ...form,
        time: normalizeTimeText(form.time) || null,
        end_time: normalizeTimeText(form.end_time) || null,
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  }

  const swipeBack = useSwipeBack(onCancel);

  return (
    <main className="agm-editor-shell" {...swipeBack}>
      <header className="agm-editor-header">
        <div className="agm-editor-top">
          <button type="button" className="agm-back-btn" onClick={onCancel}>‹</button>
          <span className="agm-editor-top-spacer" aria-hidden="true" />
          {canEdit && <button type="button" className="agm-save-btn" onClick={submit} disabled={saving}>{saving ? 'Guardando' : 'Guardar'}</button>}
        </div>
        <div className="agm-editor-title-block">
          <p>{form.task_date ? dayLongLabel(form.task_date) : 'Sin fecha'}</p>
          <textarea
            className="agm-editor-title-input"
            value={form.title}
            onChange={setField('title')}
            disabled={disabled}
            placeholder="Nueva tarea"
            rows={2}
          />
        </div>
      </header>

      <section className="agm-editor-card agm-editor-info">
        <div className="agm-editor-line agm-editor-line--two">
          <label>
            <span>Fecha</span>
            <input type="date" value={form.task_date} onChange={setField('task_date')} disabled={disabled} />
          </label>
          <label>
            <span>Estado</span>
            <select value={form.status} onChange={setField('status')} disabled={disabled}>
              <option value="pendiente">Pendiente</option>
              <option value="finalizado">Finalizado</option>
            </select>
          </label>
        </div>

        <div className="agm-editor-line agm-editor-line--two">
          <label>
            <span>Inicio</span>
            <input inputMode="numeric" placeholder="HH:MM" value={form.time} onChange={setField('time')} disabled={disabled} />
          </label>
          <label>
            <span>Fin</span>
            <input inputMode="numeric" placeholder="HH:MM" value={form.end_time} onChange={setField('end_time')} disabled={disabled} />
          </label>
        </div>

        <label>
          <span>Responsable</span>
          <input value={form.owner} onChange={setField('owner')} disabled={disabled} placeholder="Responsable" />
        </label>

        <label>
          <span>Notas</span>
          <textarea value={form.notes} onChange={setField('notes')} disabled={disabled} placeholder="Notas de la tarea..." />
        </label>
      </section>

      <section className="agm-editor-card agm-editor-assignments">
        <div className="agm-editor-card-head">
          <h2>Asignaciones</h2>
          {!isCreating && <PersonnelPdfButton entityType="task" entityId={task.id} />}
        </div>
        {isCreating ? (
          <p className="agm-editor-empty-copy">Guardá la tarea para cargar asignaciones.</p>
        ) : (
          <StaffEditor
            taskId={task.id}
            initialAssignments={task.staff_assignments || []}
            initialSections={task.staff_sections || []}
            assignmentModel={assignmentModel}
            assignmentModelError={assignmentModelError}
            retryAssignmentModel={retryAssignmentModel}
            personnel={personnel || []}
            canEdit={canEditAssignments}
            tabGroup={staffTabGroup}
            tabIndexStart={20}
            entityDate={form.task_date}
            startTime={form.time}
            endTime={form.end_time}
            touchPicker
          />
        )}
      </section>

      {error && <p className="agm-editor-error">{error}</p>}
    </main>
  );
}
